/*
 * kb-free-swatches.js — v8.9.15.8
 * ============================================================================
 * Bulletproof handle-list approach. Eligibility is determined by matching
 * the card's data-handle (or product.json handle on PDP) against a
 * configurable comma-separated list, eliminating dependency on product tags
 * in Boost AI Search's product JSON.
 *
 * Collection page: cards (matched by .bc-sf-filter-product-item + data-handle)
 *   get a FREE badge + $0.00 strikethrough + Add button when eligible.
 * PDP page: the bar shows at top; the actual price stays as the product's
 *   real price (the Shopify automatic discount handles the math at cart level).
 */
(function () {
  'use strict';

  var bar = document.getElementById('kb-free-swatches-bar');
  if (!bar) return;

  var THRESHOLD = parseInt(bar.dataset.threshold, 10) || 3;
  var FULL_PRICE_LABEL = bar.dataset.price || '$2.00';
  var IS_PDP = bar.classList.contains('kb-free-swatches-bar--pdp');

  // Parse the eligible handles list. Belt-and-suspenders: data attr first,
  // then a hardcoded fallback if the attribute is empty (handles the case
  // where the theme setting renders as empty string and Liquid's |default:
  // doesn't fire).
  var FALLBACK_HANDLES = [
    'luxe-chiffon-fabric-swatches',
    'luxe-satin-fabric-swatches',
    'satin-fabric-swatches',
    'velvet-fabric-swatches'
  ];
  var ELIGIBLE_HANDLES = (bar.dataset.handles || '')
    .split(',')
    .map(function (s) { return s.trim().toLowerCase(); })
    .filter(Boolean);
  if (ELIGIBLE_HANDLES.length === 0) {
    ELIGIBLE_HANDLES = FALLBACK_HANDLES.slice();
  }

  function isEligibleHandle(handle) {
    if (!handle) return false;
    return ELIGIBLE_HANDLES.indexOf(handle.toLowerCase()) !== -1;
  }

  // --- State ---------------------------------------------------------------
  var cartEligibleCount = 0;
  var cartTotalItems = 0;

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return { items: [] }; });
  }

  function updateCartCounts(cart) {
    cartEligibleCount = 0;
    cartTotalItems = (cart && cart.items) ? cart.items.length : 0;
    if (!cart || !cart.items) return;
    cart.items.forEach(function (item) {
      if (isEligibleHandle(item.handle)) cartEligibleCount += item.quantity;
    });
  }

  // --- Collection card processing -----------------------------------------
  var CARD_SELECTOR = '.bc-sf-filter-product-item';
  var PRICE_SELECTOR = '.bc-sf-filter-product-item-price';
  var BOTTOM_SELECTOR = '.bc-sf-filter-product-bottom';
  var GRID_SELECTOR = '#bc-sf-filter-products, .bc-sf-filter-product-grid';

  function getCardHandle(card) {
    if (card.dataset && card.dataset.handle) return card.dataset.handle;
    var s = card.querySelector('script.bc-sf-filter-product-script');
    if (s) {
      try { return JSON.parse(s.textContent).handle; } catch (e) {}
    }
    return null;
  }

  function getCardVariantId(card) {
    var s = card.querySelector('script.bc-sf-filter-product-script');
    if (!s) return null;
    try {
      var d = JSON.parse(s.textContent);
      return d.variants && d.variants[0] && d.variants[0].id;
    } catch (e) { return null; }
  }

  function processCard(card) {
    if (card.dataset.kbSwatchProcessed === '1') { renderCardState(card); return; }
    var handle = getCardHandle(card);
    if (!handle) return;
    card.dataset.kbHandle = handle;
    if (isEligibleHandle(handle)) {
      injectCardUI(card);
      card.dataset.kbEligible = '1';
    } else {
      card.dataset.kbEligible = '0';
    }
    card.dataset.kbSwatchProcessed = '1';
    renderCardState(card);
  }

  function injectCardUI(card) {
    // v8.9.15.4: minimal injection — no badge, no Add button. We only mutate
    // the price element. Cache original price HTML so we can restore when the
    // customer hits the free threshold.
    var priceEl = card.querySelector(PRICE_SELECTOR);
    if (priceEl) card.dataset.kbOriginalPrice = priceEl.innerHTML;
  }

  function renderCardState(card) {
    if (card.dataset.kbEligible !== '1') return;
    var priceEl = card.querySelector(PRICE_SELECTOR);
    if (!priceEl) return;
    var underThreshold = cartEligibleCount < THRESHOLD;
    if (underThreshold) {
      // v8.9.15.4: native sale pattern — <s>$2.00</s> $0.00
      priceEl.innerHTML =
        '<s>' + FULL_PRICE_LABEL + '</s> ' +
        '<span class="bc-sf-filter-product-item-sale-price">$0.00</span>';
    } else if (card.dataset.kbOriginalPrice) {
      priceEl.innerHTML = card.dataset.kbOriginalPrice;
    }
  }

  function handleAddClick(card, btn) {
    if (btn.disabled) return;
    var variantId = getCardVariantId(card);
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        updateCartCounts(cart);
        document.querySelectorAll(CARD_SELECTOR).forEach(renderCardState);
        updateBar();
        btn.textContent = 'Added ✓';
        btn.dataset.added = '1';
        setTimeout(function () {
          btn.textContent = 'Add';
          btn.disabled = false;
          delete btn.dataset.added;
        }, 1400);
      })
      .catch(function () {
        btn.textContent = 'Try again';
        btn.disabled = false;
      });
  }

  // --- Bar -----------------------------------------------------------------
  var copyEl = bar.querySelector('[data-counter-copy]');
  var fillEl = bar.querySelector('[data-counter-fill]');
  var viewCartEl = bar.querySelector('[data-view-cart]');
  function updateBar() {
    if (!copyEl) return;
    var pct = Math.min(100, Math.round((cartEligibleCount / THRESHOLD) * 100));
    if (fillEl) fillEl.style.width = pct + '%';
    var remaining = THRESHOLD - cartEligibleCount;
    // v8.9.15.7: shorter copy. Hide progress bar entirely once threshold met.
    var progressEl = bar.querySelector('.kb-free-swatches-bar__progress');
    if (cartEligibleCount === 0) {
      copyEl.textContent = 'Add ' + THRESHOLD + ' free swatches';
      if (progressEl) progressEl.style.display = '';
    } else if (remaining > 0) {
      copyEl.textContent = remaining + ' free swatch' + (remaining === 1 ? '' : 'es') + ' left';
      if (progressEl) progressEl.style.display = '';
    } else {
      copyEl.textContent = 'Your Swatches are ready for checkout! Extra swatches are $2.';
      if (progressEl) progressEl.style.display = 'none';
    }
    if (viewCartEl) viewCartEl.hidden = false;
  }

  // --- Boot ----------------------------------------------------------------
  function bootCollection() {
    function scanCards() {
      document.querySelectorAll(CARD_SELECTOR).forEach(processCard);
    }
    fetchCart().then(function (cart) {
      updateCartCounts(cart);
      scanCards();
      updateBar();
    });
    var grid = document.querySelector(GRID_SELECTOR) || document.body;
    new MutationObserver(function (mutations) {
      var needsScan = false;
      for (var i = 0; i < mutations.length; i++) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var n = mutations[i].addedNodes[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches(CARD_SELECTOR)) ||
              (n.querySelector && n.querySelector(CARD_SELECTOR))) {
            needsScan = true; break;
          }
        }
        if (needsScan) break;
      }
      if (needsScan) {
        scanCards();
        document.querySelectorAll(CARD_SELECTOR).forEach(renderCardState);
        updateBar();
      }
    }).observe(grid, { childList: true, subtree: true });
    window.addEventListener('focus', function () {
      fetchCart().then(function (cart) {
        updateCartCounts(cart);
        document.querySelectorAll(CARD_SELECTOR).forEach(renderCardState);
        updateBar();
      });
    });
  }

  function getPDPHandle() {
    var m = location.pathname.match(/\/products\/([^/\?]+)/);
    return m ? m[1].toLowerCase() : null;
  }
  function findPDPPriceEl() {
    // v8.9.15.5: confirmed selector on swatch PDPs (color-swatch-2 template).
    // Order: KB swatch PDP first (.section__price.js-price), then fallbacks.
    return (
      document.querySelector('.section__price.js-price') ||
      document.querySelector('.section__price') ||
      document.querySelector('.product-single__price') ||
      document.querySelector('.product__price') ||
      document.querySelector('[data-product-price]') ||
      document.querySelector('.price__amount')
    );
  }
  var pdpEligible = false;
  var pdpOriginalPriceHTML = null;
  function renderPDPState() {
    if (!pdpEligible) return;
    var priceEl = findPDPPriceEl();
    if (!priceEl) return;
    var underThreshold = cartEligibleCount < THRESHOLD;
    if (underThreshold) {
      // v8.9.15.6: preserve native .price__amount > .money structure so the
      // $0.00 inherits the same font, weight, and sizing as the regular price
      // on dress PDPs (no custom font-weight overrides).
      priceEl.innerHTML =
        '<span class="price__amount kb-sw-pdp-strike-wrap">' +
          '<span class="money" style="text-decoration:line-through;color:#1B1B1B;">' + FULL_PRICE_LABEL + '</span>' +
        '</span> ' +
        '<span class="price__amount kb-sw-pdp-free-wrap">' +
          '<span class="money" style="color:#d33;">$0.00</span>' +
        '</span>';
      // Flag so the variant-change observer knows our content is current
      priceEl.dataset.kbApplied = '1';
    } else if (pdpOriginalPriceHTML != null) {
      priceEl.innerHTML = pdpOriginalPriceHTML;
      delete priceEl.dataset.kbApplied;
    }
  }
  function bootPDP() {
    var handle = getPDPHandle();
    pdpEligible = isEligibleHandle(handle);
    if (pdpEligible) {
      var priceEl = findPDPPriceEl();
      if (priceEl) {
        pdpOriginalPriceHTML = priceEl.innerHTML;
        // v8.9.15.6: KB's variant-change handler rewrites .section__price
        // innerHTML when a color chip is clicked. Watch for that mutation and
        // re-apply our $0.00 display. Use a debounced observer to avoid loops.
        var reapplying = false;
        var mo = new MutationObserver(function () {
          if (reapplying) return;
          if (!pdpEligible) return;
          if (cartEligibleCount >= THRESHOLD) return;
          // If our markup is still there (data-kbApplied), no need to re-apply
          if (priceEl.dataset.kbApplied === '1' && priceEl.innerHTML.indexOf('kb-sw-pdp-strike-wrap') !== -1) return;
          reapplying = true;
          // Update cached original so future "restore" uses the latest variant
          // price (different variants may have different prices long-term).
          pdpOriginalPriceHTML = priceEl.innerHTML;
          renderPDPState();
          setTimeout(function () { reapplying = false; }, 50);
        });
        mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
      }
    }
    fetchCart().then(function (cart) {
      updateCartCounts(cart);
      renderPDPState();
      updateBar();
    });
    window.addEventListener('focus', function () {
      fetchCart().then(function (cart) {
        updateCartCounts(cart);
        renderPDPState();
        updateBar();
      });
    });
    document.addEventListener('kb:cart:updated', function () {
      fetchCart().then(function (cart) {
        updateCartCounts(cart);
        renderPDPState();
        updateBar();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', IS_PDP ? bootPDP : bootCollection);
  } else {
    if (IS_PDP) bootPDP(); else bootCollection();
  }
})();
